/**
 * useLiveMode —— admin dashboard 是否走「实时」轮询。
 *
 * 默认关：业界 admin 工具普遍这么做（Sentry / Datadog / Vercel）。让用户看的
 * 是「上次刷新到现在」的快照；要更新数据 → 切回 tab（SWR `revalidateOnFocus`
 * 兜底）或手动点刷新按钮。需要盯盘的运维场景再开「实时」开关。
 *
 * 选择持久到 localStorage，跨 tab 由 `storage` 事件同步 —— 跟 useEnvironment
 * 同样的模式。
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "admin-dashboard-live-mode";

/** 实时模式打开时各类轮询使用的间隔（ms）。
 * 慢变内容 60s 一次，观测数据 30s 一次。 */
export const LIVE_REFRESH_INTERVAL_STATIC_MS = 60_000;
export const LIVE_REFRESH_INTERVAL_OBSERVABILITY_MS = 30_000;

function readStored(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function useLiveMode(): [boolean, (next: boolean) => void] {
  const [live, setLiveState] = useState<boolean>(readStored);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setLiveState(e.newValue === "true");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setLive = useCallback((next: boolean) => {
    setLiveState(next);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, next ? "true" : "false");
      }
    } catch {
      // 持久化失败不阻断 UI；当前会话仍然生效
    }
  }, []);

  return [live, setLive];
}

/** 把 SWR refreshInterval 包成 "live 模式才打开"。
 * `0` 在 SWR 里语义就是 "不轮询"，刚好是默认关时想要的行为。 */
export function liveRefreshInterval(live: boolean, intervalMs: number): number {
  return live ? intervalMs : 0;
}
